/**
 * TIME ZONES, AND WHY THE PLATFORM HAS TO KNOW ONE.
 *
 * Sessions are stored as `timestamptz`, which fixes the instant and says nothing about how to show
 * it. Two questions on this platform can only be answered in the LEAGUE's own zone:
 *
 *   "Which weekday is this?"   A 7pm Monday in Denver is Tuesday in UTC. The public site once
 *                                rendered "Mondays" directly above "Starts Sunday, January 10".
 *   "What time does it start?"   Never published at all, because a time in the wrong zone is worse
 *                                than no time.
 *
 * A hardcoded `America/New_York` answered both while every market was Eastern. It has been wrong
 * three times already and stops being defensible the moment a league opens in Denver — which is the
 * stated plan.
 *
 * PURE, like the other engines here. Formatting takes a zone and a date and returns a string; it
 * reads no clock of its own and has no default. A default is what got us here.
 */

/**
 * The zones a US market can be in.
 *
 * Deliberately short. This is not every IANA name — it is the ones a league could plausibly run in,
 * offered as a list so a market cannot be given a zone that does not exist or a spelling that
 * silently falls back to UTC.
 *
 * ⚠ THIS LIST CANNOT BE DERIVED FROM A STATE. Thirteen states span two zones, and one of them is
 * already a market: NASHVILLE IS CENTRAL, Knoxville is Eastern, both are Tennessee. Whoever creates
 * a market picks from this; nothing guesses.
 */
export const US_TIME_ZONES = [
  { zone: 'America/New_York', label: 'Eastern' },
  { zone: 'America/Chicago', label: 'Central' },
  { zone: 'America/Denver', label: 'Mountain' },
  /** Arizona does not observe daylight saving, so it is its own zone for half the year. */
  { zone: 'America/Phoenix', label: 'Arizona (no daylight saving)' },
  { zone: 'America/Los_Angeles', label: 'Pacific' },
  { zone: 'America/Anchorage', label: 'Alaska' },
  { zone: 'Pacific/Honolulu', label: 'Hawaii' },
] as const;

export type TimeZone = (typeof US_TIME_ZONES)[number]['zone'];

const ZONES = new Set<string>(US_TIME_ZONES.map((z) => z.zone));

/** Whether a stored string is a zone this platform offers. Used at the edges, never assumed. */
export function isTimeZone(value: string): value is TimeZone {
  return ZONES.has(value);
}

/** "Eastern", for showing a director which zone her league is being read in. */
export function timeZoneLabel(zone: string): string {
  return US_TIME_ZONES.find((z) => z.zone === zone)?.label ?? zone;
}

/**
 * WHICH WEEKNIGHT A SESSION FALLS ON, in the league's own zone.
 *
 * 0 = Sunday. The number the schema stores on `leagues.weekday` and the number every "Mondays"
 * label is derived from. Computed rather than trusted, because the stored weekday is derived from
 * session one and can only be right if it was derived in the right zone.
 */
export function weekdayIn(date: Date, zone: string): number {
  const name = new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short' }).format(date);
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(name);
}

/**
 * A date as a reader in the league's city would write it.
 *
 * The zone is REQUIRED and has no default. Every caller has a league, every league has a market,
 * and every market now has a zone — so there is no case where the right answer is unknown, and a
 * default would only let somebody skip asking.
 */
export function formatDate(
  date: Date,
  zone: string,
  opts: Intl.DateTimeFormatOptions = { weekday: 'long', month: 'long', day: 'numeric' },
): string {
  return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: zone }).format(date);
}

/**
 * A start time, in the league's zone, with the zone named.
 *
 * "7:00 PM CT" rather than "7:00 PM". The suffix is not decoration: the whole reason this platform
 * would not publish a start time is that a bare one is unreadable to anybody who does not already
 * know which zone it is in — which is exactly the person reading a national site.
 */
export function formatTime(date: Date, zone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: zone,
    timeZoneName: 'short',
  }).format(date);
}

/**
 * THE INSTANT AT WHICH IT IS 7PM IN A GIVEN CITY.
 *
 * Everything above turns an instant into words. This goes the other way, and it is the harder half:
 * a director types "Tuesday 14 October" and means seven in the evening WHERE SHE IS, and what has to
 * be stored is the moment that happens.
 *
 * Two wrong versions of this shipped, both of which look right on the machine they were written on:
 *
 *   `new Date('2026-10-14T19:00:00')` — no suffix, so it is parsed in the BROWSER's zone. Correct in
 *   Charlotte, two hours out for a Nashville league created on a laptop a trip left on Pacific.
 *   `setUTCHours(23, …)` — 7pm Eastern in summer and SIX in winter, because a fixed UTC offset does
 *   not observe daylight saving and half the year does.
 *
 * The method is: guess, measure how far off the guess landed in that zone, and correct. Two passes,
 * because the correction can itself cross a daylight-saving boundary.
 */
export function atLocalHour(
  day: { year: number; month: number; date: number },
  zone: string,
  hour: number,
  minute = 0,
): Date {
  const wanted = Date.UTC(day.year, day.month - 1, day.date, hour, minute, 0, 0);

  let guess = wanted;
  for (let pass = 0; pass < 2; pass++) {
    /* What the guess actually reads as in that city, expressed back on the UTC number line. */
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(guess));
    const at = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    const reads = Date.UTC(at('year'), at('month') - 1, at('day'), at('hour'), at('minute'), at('second'));
    if (reads === wanted) break;
    guess += wanted - reads;
  }

  return new Date(guess);
}

/**
 * HOW MANY CALENDAR DAYS FROM `from` TO `to`, as a reader in the league's city counts them.
 *
 * 0 is the same day there, 1 is tomorrow, -1 was yesterday. Calendar days, not 24-hour periods: a
 * session at 7pm is "tomorrow" at 11pm the night before, not "in 0 days" because twenty hours is
 * less than one day. And in the LEAGUE's zone, not the phone's — the player app used to count in
 * the browser's, which told a Nashville player on an Eastern phone her session was tomorrow when it
 * was tonight.
 */
export function calendarDaysBetween(from: Date, to: Date, zone: string): number {
  const dayOf = (date: Date) => {
    const [year, month, day] = new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .format(date)
      .split('-')
      .map(Number);
    return Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1);
  };
  return Math.round((dayOf(to) - dayOf(from)) / 86_400_000);
}

/** The same, from a `YYYY-MM-DD` — the shape an `<input type="date">` hands back. */
export function atLocalHourOn(isoDate: string, zone: string, hour: number, minute = 0): Date {
  const [year, month, date] = isoDate.split('-').map(Number);
  return atLocalHour({ year: year ?? 0, month: month ?? 1, date: date ?? 1 }, zone, hour, minute);
}

/**
 * A `YYYY-MM-DD` and an `HH:MM` — the two shapes a date and a time input hand back — as the moment
 * they mean in the league's city. The start and stop times a director picks (Courtney, 2026-10-12).
 */
export function atLocalTimeOn(isoDate: string, zone: string, clock: string): Date {
  const [hour, minute] = clock.split(':').map(Number);
  return atLocalHourOn(isoDate, zone, hour ?? 0, minute ?? 0);
}

/**
 * THE SAME CLOCK TIME, WHOLE WEEKS LATER, in the league's city (2026-10-25). The date moves on the
 * calendar and the clock time is set again in the zone — so 7:00 PM stays 7:00 PM across a
 * daylight-saving change, where adding 7 × 24 hours to the instant lands an hour early or late.
 */
export function weeksOnInZone(start: Date, weeks: number, zone: string): Date {
  const [year, month, day] = dateInputIn(start, zone).split('-').map(Number);
  const date = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, (day ?? 1) + weeks * 7)).toISOString().slice(0, 10);
  return atLocalTimeOn(date, zone, timeInputIn(start, zone));
}

/**
 * THE OTHER DIRECTION: what a date input and a time input should SHOW for a stored instant, read in
 * the league's city. The Settings tab built these from the browser's clock, so a director whose
 * laptop was on another zone saw, and saved, a different evening (owner, 2026-10-12).
 */
export function dateInputIn(date: Date, zone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

export function timeInputIn(date: Date, zone: string): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(date);
}

/**
 * "6:30 – 9:00 PM MDT": a session's start and stop, in the league's zone (Courtney, 2026-10-12 —
 * directors set both). The zone is named once, at the end; AM/PM once when both share it. A session
 * with no stop time reads as its start alone.
 */
export function formatTimeRange(start: Date, end: Date | null, zone: string): string {
  if (!end) return formatTime(start, zone);
  const clock = (date: Date) =>
    new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: zone }).formatToParts(date);
  const from = clock(start);
  const to = clock(end);
  const period = (parts: Intl.DateTimeFormatPart[]) => parts.find((p) => p.type === 'dayPeriod')?.value;
  const startText = from
    .filter((p) => period(from) !== period(to) || (p.type !== 'dayPeriod' && !(p.type === 'literal' && p.value.trim() === '')))
    .map((p) => p.value)
    .join('')
    .trim();
  return `${startText} – ${formatTime(end, zone)}`;
}
